/**
 * Admin cockpit panel (M3): email outbox health (sent, skipped without a
 * Resend key, retrying, failed, suppressed), delivery events from the Resend
 * webhook, and list sizes (newsletter + founding members by double opt-in
 * status). Owner/admin only: `emailOutbox.adminStats` enforces the role.
 * German-only, like the rest of the admin cockpit.
 */
import { useMutation, useQuery } from 'convex/react'
import { Mail, RotateCw } from 'lucide-react'
import { useState } from 'react'
import { api } from '../../convex/_generated/api'
import { formatDate } from '../lib/i18n'

const STATUS_LABELS: Record<string, string> = {
  sent: 'Gesendet',
  queued: 'In Warteschlange',
  sending: 'Wird gesendet',
  retrying: 'Erneuter Versuch',
  failed: 'Fehlgeschlagen',
  skipped_no_key: 'Ohne Resend-Key',
  suppressed: 'Unterdrückt',
}

function Counts({
  title,
  counts,
  labels,
}: {
  title: string
  counts: Partial<Record<string, number>>
  labels?: Record<string, string>
}) {
  const entries = Object.entries(counts)
    .map(([key, count]): [string, number] => [key, count ?? 0])
    .sort((a, b) => b[1] - a[1])
  return (
    <div className="admin-breakdown">
      <strong>{title}</strong>
      {entries.length === 0 ? (
        <span>-</span>
      ) : (
        entries.map(([key, count]) => (
          <span key={key}>
            {labels?.[key] ?? key} <b>{count}</b>
          </span>
        ))
      )}
    </div>
  )
}

/**
 * Re-queue every `skipped_no_key` email (one batch of up to 500 per click)
 * once RESEND_API_KEY is set. Confirmations go out with a fresh token; stale
 * emails are retired. Same rules as the CLI (docs/EMAIL.md).
 */
function RequeueSkippedButton({
  skipped,
  sendingEnabled,
}: {
  skipped: number
  sendingEnabled: boolean
}) {
  const requeue = useMutation(api.emailOutbox.adminRequeueSkipped)
  const [busy, setBusy] = useState(false)
  const [message, setMessage] = useState<string | null>(null)
  if (skipped === 0 && message === null) return null

  async function run() {
    setBusy(true)
    setMessage(null)
    try {
      const result = await requeue({})
      setMessage(
        result.status === 'no_key'
          ? 'Kein RESEND_API_KEY gesetzt: es wurde nichts gesendet.'
          : `${result.requeued} erneut eingeplant, ${result.stale} zu alt, ${result.superseded} ersetzt${result.hasMore ? ' (weitere vorhanden, nochmals klicken)' : ''}.`,
      )
    } catch {
      setMessage('Erneutes Senden fehlgeschlagen. Bitte später nochmals versuchen.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="admin-requeue">
      <button
        type="button"
        className="ghost-button"
        onClick={run}
        disabled={busy || !sendingEnabled || skipped === 0}
        data-testid="admin-requeue-skipped"
      >
        <RotateCw size={16} aria-hidden="true" />
        {busy ? 'Wird eingeplant...' : `Übersprungene E-Mails senden (${skipped})`}
      </button>
      {!sendingEnabled ? (
        <small>Erst möglich, wenn RESEND_API_KEY gesetzt ist.</small>
      ) : null}
      {message ? <small role="status">{message}</small> : null}
    </div>
  )
}

export function AdminEmailPanel() {
  const stats = useQuery(api.emailOutbox.adminStats, {})

  return (
    <article className="admin-panel admin-billing-panel">
      <Mail size={22} aria-hidden="true" />
      <h2>E-Mail</h2>
      {stats === undefined ? (
        <p>E-Mail-Statistik wird geladen...</p>
      ) : (
        <>
          <p className="admin-billing-mode">
            Versand:{' '}
            <strong>
              {stats.sendingEnabled
                ? 'AKTIV (Resend-Key gesetzt)'
                : 'AUS (kein RESEND_API_KEY, E-Mails werden nur protokolliert)'}
            </strong>
          </p>
          <div className="metric-grid">
            <span>
              Newsletter aktiv <strong>{stats.newsletter.active ?? 0}</strong>
            </span>
            <span>
              Newsletter ausstehend <strong>{stats.newsletter.pending ?? 0}</strong>
            </span>
            <span>
              Abgemeldet <strong>{stats.newsletter.unsubscribed ?? 0}</strong>
            </span>
            <span>
              E-Mails 7 Tage <strong>{stats.outbox.lastSevenDays}</strong>
            </span>
          </div>
          <RequeueSkippedButton
            skipped={stats.outbox.byStatus.skipped_no_key ?? 0}
            sendingEnabled={stats.sendingEnabled}
          />
          <div className="admin-breakdowns">
            <Counts title="Outbox" counts={stats.outbox.byStatus} labels={STATUS_LABELS} />
            <Counts title="Vorlagen" counts={stats.outbox.byTemplate} />
            <Counts title="Zustellung (Webhook)" counts={stats.outbox.deliveryEvents} />
            <Counts title="Newsletter" counts={stats.newsletter} />
            <Counts title="Gründungsmitglieder" counts={stats.founding} />
            <Counts title="Sperrliste" counts={stats.suppressions} />
          </div>
          {stats.recent.length > 0 ? (
            <ul className="admin-list">
              {stats.recent.map((row) => (
                <li key={row.id}>
                  <span>
                    {row.template} an {row.to}
                  </span>
                  <small>
                    {STATUS_LABELS[row.status] ?? row.status}
                    {row.deliveryEvent ? ` / ${row.deliveryEvent}` : ''}
                    {row.attempts > 1 ? ` / ${row.attempts} Versuche` : ''}
                    {row.lastError && row.status !== 'sent' ? ` / ${row.lastError}` : ''}
                    {' / '}
                    {formatDate(row.createdAt, 'de')}
                  </small>
                </li>
              ))}
            </ul>
          ) : null}
          {stats.truncated ? (
            <small>Zählt die neuesten 5000 E-Mails.</small>
          ) : null}
        </>
      )}
    </article>
  )
}
